import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import CommonTable from "../table";
import {
  deleteCompanyFeesAction,
  getCompanyFeesAction,
} from "../../containers/dashboard/action";
import CompanyFeesModal from "./CompanyFeesModal";
import { makeStyles } from "@material-ui/core";
import theme from "../../theme";
import CommonButton from "../button";

const useStyles = makeStyles(() => ({
  headerName: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROHEAVY,
    opacity: "0.3",
    color: theme.colors.Violet,
  },
  tableContainer: {
    marginTop: "30px",
  },
  editLabel: {
    cursor: "pointer",
    color: theme.colors.DodgerBlue,
    marginRight: "10px",
    fontFamily: theme.fontFamily.MARKPROHEAVY,
    display: "flex",
    alignItems: "center",
  },
  deleteLabel: {
    cursor: "pointer",
    color: theme.colors.Pomegranate,
    fontFamily: theme.fontFamily.MARKPROHEAVY,
  },
}));

// Company fee ranges tab. The list uses history=true, so it comes back
// unpaginated — we render it as-is with no pagination control.
export default function CompanyFeesTab({ company_id }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const dashboardReducer = useSelector((state) => state.dashboardReducer);

  const getCompanyFeesMethod = React.useCallback(
    (data) => dispatch(getCompanyFeesAction(data)),
    [dispatch]
  );
  const deleteCompanyFeesMethod = React.useCallback(
    (data) => dispatch(deleteCompanyFeesAction(data)),
    [dispatch]
  );

  const [openFeesModal, setOpenFeesModal] = React.useState(false);
  const [editFeesData, setEditFeesData] = React.useState(null);
  const prevCreateSuccessRef = React.useRef(false);
  const prevUpdateSuccessRef = React.useRef(false);
  const prevDeleteSuccessRef = React.useRef(false);

  const fetchFees = React.useCallback(() => {
    if (company_id) {
      getCompanyFeesMethod({ company: company_id, history: true });
    }
  }, [company_id, getCompanyFeesMethod]);

  // Initial load / company change.
  React.useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  // Refetch after any successful mutation (create / update / delete).
  React.useEffect(() => {
    const {
      createCompanyFeesSuccess,
      updateCompanyFeesSuccess,
      deleteCompanyFeesSuccess,
    } = dashboardReducer;

    const currentCreateSuccess = !!createCompanyFeesSuccess;
    const currentUpdateSuccess = !!updateCompanyFeesSuccess;
    const currentDeleteSuccess = !!deleteCompanyFeesSuccess;

    if (currentCreateSuccess && !prevCreateSuccessRef.current) {
      prevCreateSuccessRef.current = true;
      fetchFees();
    } else if (!currentCreateSuccess) {
      prevCreateSuccessRef.current = false;
    }

    if (currentUpdateSuccess && !prevUpdateSuccessRef.current) {
      prevUpdateSuccessRef.current = true;
      fetchFees();
    } else if (!currentUpdateSuccess) {
      prevUpdateSuccessRef.current = false;
    }

    if (currentDeleteSuccess && !prevDeleteSuccessRef.current) {
      prevDeleteSuccessRef.current = true;
      fetchFees();
    } else if (!currentDeleteSuccess) {
      prevDeleteSuccessRef.current = false;
    }
  }, [dashboardReducer, fetchFees]);

  const feesData = Array.isArray(dashboardReducer?.companyFees)
    ? dashboardReducer.companyFees
    : dashboardReducer?.companyFees?.data || [];

  return (
    <>
      <div>
        <CommonButton
          type="button"
          text={"Set Up Company Fees"}
          stylevariant="secondary4"
          onClick={() => {
            setOpenFeesModal(true);
            setEditFeesData(null);
          }}
          style={{ padding: "5px 40px" }}
          fontSize="18px"
          fontFamily={theme.fontFamily.MARKPROHEAVY}
        />
      </div>
      <div className={classes.tableContainer}>
        <CommonTable
          columns={[
            {
              Header: "Start Range",
              accessor: "start_range",
              HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
              Cell: (data) => <span>{data || "0"}</span>,
            },
            {
              Header: "End Range",
              accessor: "end_range",
              HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
              Cell: (data) => <span>{data || "0"}</span>,
            },
            {
              Header: "Fee",
              accessor: "fee",
              HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
              Cell: (data) => <span>{data || "0"}</span>,
            },
            {
              Header: "",
              accessor: "id",
              HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
              Cell: (data, row) => {
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                    <div
                      className={classes.editLabel}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenFeesModal(true);
                        setEditFeesData(row);
                      }}
                    >
                      <img
                        src={"/images/editIcon.svg"}
                        style={{ marginRight: "8px", marginBottom: "3px" }}
                        alt="icon"
                      />{" "}
                      Edit Company Fees
                    </div>
                    <div
                      className={classes.deleteLabel}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCompanyFeesMethod(row.id);
                      }}
                    >
                      Remove
                    </div>
                  </div>
                );
              },
            },
          ]}
          data={feesData}
          loading={dashboardReducer?.loading}
        />
      </div>
      <CompanyFeesModal
        openModal={openFeesModal}
        editData={editFeesData}
        onClose={() => {
          setOpenFeesModal(false);
          setEditFeesData(null);
        }}
        company_id={company_id}
      />
    </>
  );
}
